import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../contexts/LanguageContext";
import $ from "jquery";
import "datatables.net-dt/css/dataTables.dataTables.css";
import "datatables.net";
import { hasPermission } from "../../../utils/permissions";
import PageLayout from "../../../components/modern/PageLayout/PageLayout";
import { HiDocumentText, HiClipboardList, HiDocumentDuplicate, HiClock, HiChartBar } from "react-icons/hi";
import { ShipForMeForm, ShipForMeStatus, ShipForMeHistory, ShipForMeInvoices, ShipForMeSummary } from "./components";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./EditShipForMe.scss";

export default function EditShipForMe() {
  const { t, i18n } = useTranslation('shipforme');
  const { isRTL, direction } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    id: 0,
    link: "",
    merchantName: "",
    description: "",
    quantity: 1,
    width: 0,
    height: 0,
    length: 0,
    weight: 0,
    extendedDescription: "",
    destinationCountryId: 0,
    originCountryId: 0,
    paymentMethod: 0,
    categoryId: 0,
    shippingCompanyId: 0,
    merchentDeliveryFee: 0,
    orderStatus: 0,
    itemType: "",
    deliveryMode: "",
    itemPrice: 0,
    paymentMethodId: 0,
    paymentType: 0,
    servicePrice: 0,
    totalPrice: 0,
    additionaCost: 0,
    shippingCompanyPricePerLB: 0,
    ShippingCompanyId: 0,
    currencyId: 0,
    orderPrice: 0,
    exchangePrice: 0,
    diementionalWeight: 0,
    shippingCost: 0,
    shippingInssurance: 0,
    shippingFuelCost: 0,
    tax: 0,
    buyForMeCost: 0,
    paymentMethodCost: 0,
    refundType: 0,
    invoices: [],
    orderLogs: [],
    voucherValue: 0,
    hasShippingInssurance: false,
    dgFees: 0,
    enableDGFees: false,
    cityId: 0,
    localDeliveryFee: 0,
    weightMeasurementUnitId: null,
    lengthMeasurementUnitId: null
  });

  const [orderStatusForm, setOrderStatusForm] = useState({
    orderStatus: 0,
    refundType: 0,
    comment: "",
    orderId: id,
    extraField: "",
    paymentGatewayId: null as number | null
  });

  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shippingCompanys, setShippingCompanys] = useState([]);
  const [cities, setCities] = useState([]);
  const [weightMeasurementUnits, setWeightMeasurementUnits] = useState([]);
  const [lengthMeasurementUnits, setLengthMeasurementUnits] = useState([]);
  const [paymentGateways, setPaymentGateways] = useState<any[]>([]);
  const [exchangeRate, setExchangeRate] = useState(1500);
  // Financial Control: immutable net price + FX-lock metadata read from the invoice snapshot.
  const [netPriceUsd, setNetPriceUsd] = useState<number | null>(null);
  const [netPriceInIqd, setNetPriceInIqd] = useState<number | null>(null);
  const [fxLock, setFxLock] = useState<any>(null);
  const [isLBPriceEnabled, setIsLBPriceEnabled] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [calculatingOrder, setCalculatingOrder] = useState(false);
  const [resettingOrder, setResettingOrder] = useState(false);
  const [messageStatus, setMessageStatus] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchOrder();
    fetchCountries();
    fetchCurrencies();
    fetchPaymentMethods();
    fetchCategories();
    fetchCities();
    fetchMeasurementUnits();
    fetchPaymentGateways();
  }, [id, i18n.language]);

  // Fetch shipping companies only when order data is loaded with valid countries
  useEffect(() => {
    if (formData.originCountryId && formData.originCountryId > 0 &&
      formData.destinationCountryId && formData.destinationCountryId > 0) {
      fetchShippingCompanys();
    }
  }, [formData.originCountryId, formData.destinationCountryId]);

  const fetchPaymentGateways = async () => {
    try {
      const response = await adminApi.call('/paymentgateway/active', 'GET');
      if (response.success) {
        setPaymentGateways(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'Accept-Language': localStorage.getItem('language') || 'en'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(data.order);
        setExchangeRate(Number(data.exchangeRate));
        setNetPriceUsd(data.netPriceUsd ?? null);
        setNetPriceInIqd(data.netPriceInIqd ?? null);
        setFxLock(data.fxLock ?? null);
        setOrderStatusForm(prev => ({
          ...prev,
          orderStatus: data.order.orderStatus,
          refundType: data.order.refundType
        }));
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await adminApi.call('/country', 'GET');
      if (response.success) {
        setCountries(response.data?.items?.filter((x: any) => !x.isSuspended) || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await adminApi.call('/city?page=1&pageSize=1000', 'GET');
      if (response.success) {
        setCities(response.data?.items || []);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await adminApi.call('/currency', 'GET');
      if (response.success) {
        setCurrencies(response.data?.items);
      }
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await adminApi.call('/paymentmethod?pageSize=10000', 'GET');
      if (response.success) {
        setPaymentMethods(response.data?.items);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/category?pageSize=10000`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.items);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchShippingCompanys = async () => {
    try {
      // Build query parameters with filters
      const params = new URLSearchParams();
      params.append('pageSize', '10000');
      params.append('isActive', 'true');

      if (formData.originCountryId && formData.originCountryId > 0) {
        params.append('sourceCountryId', formData.originCountryId.toString());
      }

      if (formData.destinationCountryId && formData.destinationCountryId > 0) {
        params.append('destinationCountryId', formData.destinationCountryId.toString());
      }

      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/shippingCompany?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setShippingCompanys(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching shipping companies:', error);
    }
  };

  const handleInputChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked :
        name.includes("CountryId") ||
          name.includes("CategoryId") ||
          name.includes("Type") ||
          name.includes("deliveryMode") ||
          name.includes("MeasurementUnitId") ||
          name.includes("ShippingCompanyId") ||
          name.includes("CurrencyId") ||
          name.includes("PaymentMethodId") ||
          name === "initialPartialPayment" ||
          name === "quantity" ||
          name === "itemPrice" ||
          name === "merchentDeliveryFee" ||
          name === "width" ||
          name === "height" ||
          name === "length" ||
          name === "weight" ||
          name === "shippingCompanyPricePerLB" ? Number(value) : value
    }));
  };

  const fetchMeasurementUnits = async () => {
    try {
      // Fetch weight measurement units (type 1)
      const weightResponse = await fetch(
        `${import.meta.env.VITE_APP_API_BASE_URL}/measurementunit/by-type/1`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (weightResponse.ok) {
        const weightData = await weightResponse.json();
        setWeightMeasurementUnits(weightData);
      }

      // Fetch length measurement units (type 0)
      const lengthResponse = await fetch(
        `${import.meta.env.VITE_APP_API_BASE_URL}/measurementunit/by-type/0`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (lengthResponse.ok) {
        const lengthData = await lengthResponse.json();
        setLengthMeasurementUnits(lengthData);
      }
    } catch (error) {
      console.error("Error fetching measurement units:", error);
    }
  };

  const handleQuantityChange = (increment: number) => {
    setFormData(prevData => ({
      ...prevData,
      quantity: Math.max(1, prevData.quantity + increment)
    }));
  };

  const handleStatusFormInputChange = (event: any) => {
    const { name, value } = event.target;
    setOrderStatusForm(prevData => ({
      ...prevData,
      [name]: name.includes("orderStatus") || name.includes("refundType") ? Number(value) : value
    }));
  };

  const updateOrder = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showSuccessToast(t('messages.orderUpdated'));
        fetchOrder();
      } else {
        const errorData = await response.json();
        showErrorToast(errorData.message || t('messages.updateFailed'));
      }
    } catch (error) {
      console.error('Error updating order:', error);
      showErrorToast(t('messages.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const calculateOrder = async () => {
    setCalculatingOrder(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/calculate/${id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        showSuccessToast(t('messages.orderCalculated'));
        fetchOrder();
      } else {
        showErrorToast(data.message || t('messages.calculationFailed'));
      }
    } catch (error) {
      console.error('Error calculating order:', error);
      showErrorToast(t('messages.calculationFailed'));
    } finally {
      setCalculatingOrder(false);
    }
  };

  const resetOrder = async () => {
    setResettingOrder(true);
    const resetStatusForm = {
      ...orderStatusForm,
      orderStatus: 0
    };
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/updatestatus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(resetStatusForm)
      });

      if (response.ok) {
        showSuccessToast(t('messages.orderReset'));
        fetchOrder();
      } else {
        showErrorToast(t('messages.resetFailed'));
      }
    } catch (error) {
      console.error('Error resetting order:', error);
      showErrorToast(t('messages.resetFailed'));
    } finally {
      setResettingOrder(false);
    }
  };

  const handleStatusUpdate = async (status: number, comment?: string) => {
    const trimmedComment = (comment || "").trim();
    if (formData.orderStatus === 0 && status === 2 && !trimmedComment) {
      showErrorToast("Note is required when rejecting a pending order");
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/updatestatus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: formData.id,
          orderStatus: status,
          comment: trimmedComment,
        })
      });

      if (response.ok) {
        showSuccessToast(t('messages.statusUpdated'));
        setFormData(prevData => ({
          ...prevData,
          orderStatus: status
        }));
      } else {
        showErrorToast(t('messages.statusUpdateFailed'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showErrorToast(t('messages.statusUpdateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const confirmPendingStatus = () => {
    const status = Number(orderStatusForm.orderStatus);
    const paymentType = formData?.paymentType;
    const hasExtraField = !!getExtraFieldLabel();
    const isInvoiceRelatedExtraField =
      ((status === 10 || status === 31) && (paymentType === 2 || paymentType === 1)) ||
      (status === 25 && (paymentType === 0 || paymentType === 1));

    const sortedInvoicesByCreatedAtAsc = Array.isArray(formData?.invoices)
      ? [...formData.invoices].sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      : [];

    let invoiceForCurrentStatus: any = null;
    if (sortedInvoicesByCreatedAtAsc.length > 0) {
      if (paymentType === 1) {
        if (status === 10 || status === 31) {
          invoiceForCurrentStatus = sortedInvoicesByCreatedAtAsc[0] ?? null;
        } else if (status === 25) {
          invoiceForCurrentStatus = sortedInvoicesByCreatedAtAsc[1] ?? sortedInvoicesByCreatedAtAsc[0] ?? null;
        } else {
          invoiceForCurrentStatus = sortedInvoicesByCreatedAtAsc[sortedInvoicesByCreatedAtAsc.length - 1] ?? null;
        }
      } else {
        invoiceForCurrentStatus = sortedInvoicesByCreatedAtAsc[sortedInvoicesByCreatedAtAsc.length - 1] ?? null;
      }
    }

    const shouldRequireExtraField = hasExtraField && (!isInvoiceRelatedExtraField || !invoiceForCurrentStatus?.isCollected);

    if (orderStatusForm.orderStatus == 0) {
      setConfirmMessage(t('modal.confirmStatusChangeMessage'));
      $('#confirmPendingModal').modal('show');
      return;
    }

    if (shouldRequireExtraField && !orderStatusForm.extraField?.trim()) {
      setMessageStatus(`${getExtraFieldLabel()} is required`);
      return;
    }
    updateOrderStatus();
  };

  const updateOrderStatus = async () => {
    setUpdatingStatus(true);
    setMessageStatus("");
    try {
      const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL}/adminshipforme/updatestatus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderStatusForm)
      });

      if (response.ok) {
        setMessageStatus(t('messages.statusUpdated'));
        fetchOrder();
        // Clear comment and extraField after successful save
        setOrderStatusForm((prevData) => ({
          ...prevData,
          comment: "",
          extraField: "",
        }));
        try {
          $('#confirmPendingModal').modal('hide');
        } catch (error) {
          console.error('Error hiding modal:', error);
        }
      } else {
        const errorData = await response.json();
        setMessageStatus(errorData.message || t('messages.statusUpdateFailed'));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setMessageStatus(t('messages.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getExtraFieldLabel = () => {
    if (orderStatusForm.refundType == 2) return null;

    const status = Number(orderStatusForm.orderStatus);
    const paymentType = formData?.paymentType;

    if (status === 31 && (paymentType === 2 || paymentType === 1)) return t('extraFieldLabels.courierReceiptNo');
    if (status === 13) return t('extraFieldLabels.orderNumber');
    if (status === 20) return t('extraFieldLabels.shippingOrderNo');
    if (status === 25 && (paymentType === 0 || paymentType === 1)) return t('extraFieldLabels.courierReceiptNo');
    if (status === 19) return t('extraFieldLabels.reorderNumber');
    if (status === 18) return t('extraFieldLabels.refundReceiptNo');

    return null;
  };

  const getStatusBadge = (status: number) => {
    const statusConfig = {
      0: { color: 'warning', text: t('statusOptions.0') },
      1: { color: 'info', text: t('statusOptions.1') },
      2: { color: 'danger', text: t('statusOptions.2') },
      7: { color: 'primary', text: t('statusOptions.7') },
      8: { color: 'secondary', text: t('statusOptions.8') },
      9: { color: 'info', text: t('statusOptions.9') },
      10: { color: 'primary', text: t('statusOptions.10') },
      11: { color: 'warning', text: t('statusOptions.11') },
      12: { color: 'danger', text: t('statusOptions.12') },
      13: { color: 'success', text: t('statusOptions.13') },
      14: { color: 'info', text: t('statusOptions.14') },
      15: { color: 'primary', text: t('statusOptions.15') },
      16: { color: 'danger', text: t('statusOptions.16') },
      17: { color: 'warning', text: t('statusOptions.17') },
      18: { color: 'success', text: t('statusOptions.18') },
      20: { color: 'info', text: t('statusOptions.20') },
      21: { color: 'primary', text: t('statusOptions.21') },
      22: { color: 'warning', text: t('statusOptions.22') },
      23: { color: 'success', text: t('statusOptions.23') },
      24: { color: 'info', text: t('statusOptions.24') },
      25: { color: 'success', text: t('statusOptions.25') },
      26: { color: 'danger', text: t('statusOptions.26') },
      27: { color: 'warning', text: t('statusOptions.27') },
      28: { color: 'danger', text: t('statusOptions.28') },
      29: { color: 'danger', text: t('statusOptions.29') },
      30: { color: 'info', text: t('statusOptions.30') },
      31: { color: 'primary', text: t('statusOptions.31') }
    };

    const config = statusConfig[status] || { color: 'secondary', text: t('statusOptions.unknown') };
    return (
      <span className={`badge bg-${config.color} m-2`}>
        {config.text}
      </span>
    );
  };

  const breadcrumbs = [
    { path: '/shipforme', label: t('shipforme:breadcrumbs.shipforme') },
    { label: `${t('editOrder')} #${formData.id}` }
  ];

  return (
    <PageLayout
      title={`${t('editOrder')} #${formData.id}`}
      breadcrumbs={breadcrumbs}

    >

      {/* Tabbed Interface */}
      <div className={`card shadow-lg border-0 rounded-3 ${isRTL ? 'rtl' : ''}`} dir={direction}>
        <div className="card-header bg-light">
          <ul className={`nav nav-tabs card-header-tabs ${isRTL ? 'rtl-tabs' : ''}`} id="shipForMeTabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className="nav-link active"
                id="form-tab"
                data-bs-toggle="tab"
                data-bs-target="#form-tab-pane"
                type="button"
                role="tab"
                aria-controls="form-tab-pane"
                aria-selected="true"
              >
                <HiDocumentText className={`${isRTL ? 'ms-2' : 'me-2'}`} />
                {t('tabs.coreFormFields')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link"
                id="status-tab"
                data-bs-toggle="tab"
                data-bs-target="#status-tab-pane"
                type="button"
                role="tab"
                aria-controls="status-tab-pane"
                aria-selected="false"
              >
                <HiClipboardList className={`${isRTL ? 'ms-2' : 'me-2'}`} />
                {t('tabs.statusManagement')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link"
                id="invoices-tab"
                data-bs-toggle="tab"
                data-bs-target="#invoices-tab-pane"
                type="button"
                role="tab"
                aria-controls="invoices-tab-pane"
                aria-selected="false"
              >
                <HiDocumentDuplicate className={`${isRTL ? 'ms-2' : 'me-2'}`} />
                {t('tabs.invoiceManagement')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link"
                id="history-tab"
                data-bs-toggle="tab"
                data-bs-target="#history-tab-pane"
                type="button"
                role="tab"
                aria-controls="history-tab-pane"
                aria-selected="false"
              >
                <HiClock className={`${isRTL ? 'ms-2' : 'me-2'}`} />
                {t('tabs.shipmentHistory')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className="nav-link"
                id="summary-tab"
                data-bs-toggle="tab"
                data-bs-target="#summary-tab-pane"
                type="button"
                role="tab"
                aria-controls="summary-tab-pane"
                aria-selected="false"
              >
                <HiChartBar className={`${isRTL ? 'ms-2' : 'me-2'}`} />
                {t('tabs.shipmentSummary')}
              </button>
            </li>
          </ul>
        </div>

        <div className="card-body p-0">
          <div className="tab-content" id="shipForMeTabsContent">
            {/* Core Form Fields Tab */}
            <div
              className="tab-pane fade show active"
              id="form-tab-pane"
              role="tabpanel"
              aria-labelledby="form-tab"
              tabIndex={0}
            >
              <div className="p-4">
                <ShipForMeForm
                  formData={formData}
                  countries={countries}
                  currencies={currencies}
                  categories={categories}
                  shippingCompanys={shippingCompanys}
                  cities={cities}
                  weightMeasurementUnits={weightMeasurementUnits}
                  lengthMeasurementUnits={lengthMeasurementUnits}
                  isLBPriceEnabled={isLBPriceEnabled}
                  setIsLBPriceEnabled={setIsLBPriceEnabled}
                  handleInputChange={handleInputChange}
                  handleQuantityChange={handleQuantityChange}
                  updateOrder={updateOrder}
                  calculateOrder={calculateOrder}
                  resetOrder={resetOrder}
                  handleStatusUpdate={handleStatusUpdate}
                  updating={updating}
                  calculatingOrder={calculatingOrder}
                  resettingOrder={resettingOrder}
                  id={id}
                />
              </div>
            </div>

            {/* Status Management Tab */}
            <div
              className="tab-pane fade"
              id="status-tab-pane"
              role="tabpanel"
              aria-labelledby="status-tab"
              tabIndex={0}
            >
              <div className="p-4">
                <ShipForMeStatus
                  formData={formData}
                  orderStatusForm={{ ...orderStatusForm, paymentGateways: paymentGateways }}
                  handleStatusFormInputChange={handleStatusFormInputChange}
                  confirmPendingStatus={confirmPendingStatus}
                  getExtraFieldLabel={getExtraFieldLabel}
                  getStatusBadge={getStatusBadge}
                  updatingStatus={updatingStatus}
                  messageStatus={messageStatus}
                  confirmMessage={confirmMessage}
                  updateOrderStatus={updateOrderStatus}
                  onReload={fetchOrder}
                />
              </div>
            </div>

            {/* Invoice Management Tab */}
            <div
              className="tab-pane fade"
              id="invoices-tab-pane"
              role="tabpanel"
              aria-labelledby="invoices-tab"
              tabIndex={0}
            >
              <div className="p-4">
                <ShipForMeInvoices
                  formData={formData}
                  exchangeRate={exchangeRate}
                  id={id}
                />
              </div>
            </div>

            {/* Shipment History Tab */}
            <div
              className="tab-pane fade"
              id="history-tab-pane"
              role="tabpanel"
              aria-labelledby="history-tab"
              tabIndex={0}
            >
              <div className="p-4">
                <ShipForMeHistory
                  formData={formData}
                  selectedLog={selectedLog}
                  setSelectedLog={setSelectedLog}
                />
              </div>
            </div>

            {/* Shipment Summary Tab */}
            <div
              className="tab-pane fade"
              id="summary-tab-pane"
              role="tabpanel"
              aria-labelledby="summary-tab"
              tabIndex={0}
            >
              <div className="p-4">
                <ShipForMeSummary
                  formData={formData}
                  exchangeRate={exchangeRate}
                  netPriceUsd={netPriceUsd}
                  netPriceInIqd={netPriceInIqd}
                  fxLock={fxLock}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
